import React from 'react';
import { parseIndustries } from '@local/finsera-core/src/helpers';
import { useEffectWithoutFirst } from '@local/finsera-core/src/hooks';
import { mui, useSelector } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import CheckboxesAutocomplete from '../../../checkboxes-autocomplete';
import { getConstraintType } from '../../optimizer-utils/constraint-type';
import Bounds from '../bounds';
import SharedConstraintFields from '../shared-fields';
import Target from '../target';

type IndustryConstraintProps = {
  id: string;
  params: types.optimizer.IndustryDraft;
  setParams: (_params: types.optimizer.IndustryDraft) => void;
  readOnly: boolean;
  optimizerType: 'basket' | 'backtest';
  validationDef: types.common.ValidationErrors;
};

const IndustryConstraint = ({
  id,
  params,
  setParams,
  readOnly,
  optimizerType,
  validationDef,
}: IndustryConstraintProps) => {
  const [showMore, setShowMore] = React.useState(false);
  const [constraint, setConstraint] = React.useState(params);

  const resources = useSelector((state) => state.resources);
  const theme = mui.styles.useTheme() as mui.core.Theme;

  React.useEffect(() => {
    if (optimizerType == 'basket' && params.benchmark_relative == null) {
      setConstraint({ ...constraint, benchmark_relative: true });
    }
  }, []);

  useEffectWithoutFirst(() => {
    setParams(constraint);
  }, [constraint]);

  const form = () => {
    if (getConstraintType(constraint) == 'BOUNDS') {
      if (showMore) {
        return (
          <Bounds
            id={id}
            params={constraint}
            setParams={setConstraint}
            isPercent
            readOnly={readOnly}
            validationDef={validationDef}
          >
            <SharedConstraintFields
              id={id}
              params={constraint}
              setParams={setConstraint}
              readOnly={readOnly}
              validationDef={validationDef}
            />
          </Bounds>
        );
      }

      return (
        <Bounds
          id={id}
          params={constraint}
          setParams={setConstraint}
          isPercent
          readOnly={readOnly}
          validationDef={validationDef}
        />
      );
    }

    if (showMore) {
      return (
        <Target params={constraint} setParams={setConstraint} readOnly={readOnly}>
          <SharedConstraintFields
            id={id}
            params={constraint}
            setParams={setConstraint}
            readOnly={readOnly}
            validationDef={validationDef}
          />
        </Target>
      );
    }

    return <Target params={constraint} setParams={setConstraint} readOnly={readOnly} />;
  };

  let valueOptions: { value: string; name: string; shortName: string }[] = [];
  if (params.type == 'RBICS_L1_ID') valueOptions = parseIndustries.industryGenerator(resources.rbics_data.levelOne);
  if (params.type == 'RBICS_L2_ID') valueOptions = parseIndustries.industryGenerator(resources.rbics_data.levelTwo);
  if (params.type == 'RBICS_L3_ID') valueOptions = parseIndustries.industryGenerator(resources.rbics_data.levelThree);
  if (params.type == 'RBICS_L4_ID') valueOptions = parseIndustries.industryGenerator(resources.rbics_data.levelFour);

  valueOptions = [{ value: 'select-all', name: 'select-all', shortName: 'select-all' }, ...valueOptions];

  let industryOptions: {
    value: string;
    label: string;
    deprecated?: boolean;
  }[] = [
    {
      value: 'RBICS_L1_ID',
      label: 'Sector',
    },
    {
      value: 'RBICS_L2_ID',
      label: 'Sub Sector',
    },
    {
      value: 'RBICS_L3_ID',
      label: 'Industry',
    },
    {
      value: 'RBICS_L4_ID',
      label: 'Sub Industry',
    },
  ];

  if (params?.type?.includes('GICS')) {
    industryOptions = industryOptions.concat([
      {
        value: 'GICS_SECTOR',
        label: 'Sector (Deprecated)',
        deprecated: true,
      },
      {
        value: 'GICS_GROUP',
        label: 'Group (Deprecated)',
        deprecated: true,
      },
      {
        value: 'GICS_IND',
        label: 'Industry (Deprecated)',
        deprecated: true,
      },
      {
        value: 'GICS_SUBIND',
        label: 'Sub Industry (Deprecated)',
        deprecated: true,
      },
    ]);
  }

  return (
    <>
      <mui.core.Box p={2}>
        <mui.core.Grid container spacing={5} style={{ marginBottom: theme.spacing(3) }}>
          <mui.core.Grid size={{ xs: 12, md: 3 }} >
            <mui.core.Box px={3}>
              <mui.core.FormControlLabel
                control={
                  <mui.core.Switch
                    size="small"
                    checked={constraint.benchmark_relative}
                    onChange={() =>
                      setConstraint({
                        ...constraint,
                        benchmark_relative: !constraint.benchmark_relative,
                      })
                    }
                    disabled={readOnly}
                  />
                }
                label={optimizerType == 'backtest' ? 'Benchmark Relative' : 'Relative to Target Weights'}
              />
            </mui.core.Box>
          </mui.core.Grid>
          <mui.core.Grid size={{ xs: 12, md: 4 }}>
            <mui.core.TextField
              select
              label="Type"
              variant="outlined"
              size="small"
              value={constraint.type}
              onChange={(e) =>
                setConstraint({
                  ...constraint,
                  type: e.target.value as types.optimizer.CategoryConstraint['type'],
                  value: undefined,
                })
              }
              fullWidth
              disabled={readOnly}
              required
              error={validationDef?.fields?.includes(`industry_type_${id}`)}
            >
              {industryOptions.map((indOp) => (
                <mui.core.MenuItem key={indOp.value} value={indOp.value} disabled={indOp.deprecated}>
                  {indOp.label}
                </mui.core.MenuItem>
              ))}
            </mui.core.TextField>
          </mui.core.Grid>
          <mui.core.Grid size={{ xs: 12, md: 4 }}>
            <CheckboxesAutocomplete<string>
              value={constraint.value as string[]}
              setValue={(val) => setConstraint({ ...constraint, value: val })}
              options={valueOptions}
              label="Value"
              disabled={readOnly || constraint?.type?.includes('GICS')}
              optionLabel={(o) => o.name as string}
              chipKey="shortName"
              error={validationDef?.fields?.includes(`industry_value_${id}`)}
            />
          </mui.core.Grid>
        </mui.core.Grid>
        {form()}
        <mui.core.Box pt={3}>
          <mui.core.Button
            onClick={() => {
              setShowMore(!showMore);
            }}
            color="primary"
            startIcon={showMore ? <mui.icons.Remove /> : <mui.icons.Add />}
          >
            {showMore ? <>Show Less</> : <>Show More</>}
          </mui.core.Button>
        </mui.core.Box>
      </mui.core.Box>
    </>
  );
};

export default IndustryConstraint;
